import React, { useState } from 'react';
import Header from './Header';
import Footer from './Footer';
import './FAQPage.css';

const FAQPage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqCategories = [
    {
      id: 'booking',
      title: 'Booking & Reservations',
      icon: '📅',
      faqs: [
        {
          id: 1,
          question: 'How do I book a flight?',
          answer: 'To book a flight, simply go to the "Book a Flight" page, enter your departure and destination airports, select your travel dates, choose the number of passengers and class, then click "Search Flights". Browse the available flights and select the one that suits you best. Follow the prompts to enter passenger details and complete your payment.'
        },
        {
          id: 2,
          question: 'Can I cancel or reschedule a flight?',
          answer: 'Yes, you can cancel or reschedule your flight through your dashboard under "My Trips". Select the booking you want to modify and choose either "Cancel" or "Change Flight". Please note that cancellation fees may apply based on your fare type and the timing of your cancellation. Refunds are typically processed within 5-7 business days.'
        },
        {
          id: 3,
          question: 'How do I view my booking?',
          answer: 'You can view your booking by logging into your account and going to "My Trips" in the dashboard. All your upcoming and past trips will be displayed there. You can click on any booking to see full details, download your ticket, or make changes.'
        },
        {
          id: 4,
          question: 'Can I book for multiple passengers?',
          answer: 'Yes, you can book for multiple passengers in a single booking. When searching for flights, select the number of adults, children, and infants. During the booking process, you\'ll be able to enter details for each passenger.'
        }
      ]
    },
    {
      id: 'payments',
      title: 'Payments & Refunds',
      icon: '💳',
      faqs: [
        {
          id: 5,
          question: 'What Nigerian payment options are supported?',
          answer: 'We support all major Nigerian payment methods including Paystack (Card, Bank Transfer, Wallet), Flutterwave, USSD payments for major banks (GTBank *737#, Zenith *966#, Access *901#, etc.), direct bank transfers, and Verve cards. All payments are processed securely through our trusted payment partners.'
        },
        {
          id: 6,
          question: 'How long do refunds take?',
          answer: 'Refunds to Nigerian bank accounts typically take 5-7 business days. For card payments, refunds may take 7-14 business days depending on your bank. You\'ll receive an email confirmation once the refund is processed. If you don\'t see the refund after 14 days, please contact our support team.'
        },
        {
          id: 7,
          question: 'Why was my payment declined?',
          answer: 'Common reasons for payment declines include insufficient funds, daily transaction limits set by your bank, or your bank\'s security settings blocking the transaction. Try using a different card, contact your bank to authorize the transaction, or use alternative payment methods like USSD or bank transfer.'
        },
        {
          id: 8,
          question: 'Can I pay in installments?',
          answer: 'Currently, we require full payment at the time of booking. However, we\'re working on introducing installment payment options for Nigerian travelers. Stay tuned for updates!'
        }
      ]
    },
    {
      id: 'checkin',
      title: 'Check-In & Boarding',
      icon: '✈️',
      faqs: [
        {
          id: 9,
          question: 'How do I check in online?',
          answer: 'You can check in online through your dashboard. Go to "My Trips", select your upcoming flight, and click "Check In". Online check-in is typically available 24-48 hours before your flight departure time. You\'ll receive your boarding pass via email, which you can print or save on your mobile device.'
        },
        {
          id: 10,
          question: 'Can I select my seat in advance?',
          answer: 'Yes, seat selection is available during the booking process or after booking through your dashboard. Seat selection fees may apply depending on the airline and seat type (window, aisle, exit row, etc.). You can also select seats at check-in, subject to availability.'
        },
        {
          id: 11,
          question: 'What ID is required for domestic flights?',
          answer: 'For domestic flights within Nigeria, you need a valid government-issued ID such as your National ID Card (NIN), International Passport, Driver\'s License, or Voter\'s Card. The name on your ID must match the name on your booking exactly. Children under 18 may use their birth certificate.'
        },
        {
          id: 12,
          question: 'What time should I arrive at the airport?',
          answer: 'For domestic flights, we recommend arriving at least 2 hours before your scheduled departure time. For international flights, arrive at least 3 hours early. This allows time for check-in, security screening, and boarding. Check your booking confirmation for specific airline requirements.'
        }
      ]
    },
    {
      id: 'requirements',
      title: 'Travel Requirements',
      icon: '🛡️',
      faqs: [
        {
          id: 13,
          question: 'Do I need a visa for international destinations?',
          answer: 'Visa requirements depend on your destination and nationality. Nigerian passport holders typically need visas for most international destinations. We recommend checking visa requirements well in advance of your travel date. Some countries offer visa-on-arrival for Nigerian travelers. Contact the embassy of your destination country for the most current information.'
        },
        {
          id: 14,
          question: 'What is allowed in my baggage?',
          answer: 'Baggage allowances vary by airline and fare type. Generally, economy class includes 1 carry-on bag (7kg) and 1 checked bag (23kg). Business and first class typically include more generous allowances. Prohibited items include liquids over 100ml in carry-on, sharp objects, flammable materials, and certain batteries. Check with your airline for specific restrictions.'
        },
        {
          id: 15,
          question: 'Are there health or COVID-related requirements?',
          answer: 'Health requirements vary by destination and are subject to change. Some countries may require vaccination certificates, negative COVID-19 test results, or health declaration forms. We recommend checking the latest health requirements for your destination at least 2 weeks before travel. Visit the Nigerian Ministry of Health or your destination country\'s health ministry website for current guidelines.'
        },
        {
          id: 16,
          question: 'What documents do I need for international travel?',
          answer: 'For international travel, you\'ll need a valid passport (with at least 6 months validity remaining), a valid visa (if required), your flight ticket, and any health certificates or vaccination records required by your destination. Some countries also require proof of accommodation and return ticket. Always check specific requirements for your destination.'
        }
      ]
    }
  ];

  const toggleFAQ = (categoryId, faqId) => {
    const key = `${categoryId}-${faqId}`;
    setOpenFAQ(openFAQ === key ? null : key);
  };

  const filteredCategories = faqCategories;

  return (
    <div className="faq-page">
      <Header />
      
      {/* Hero Section */}
      <section className="faq-hero-section">
        <div className="page-container">
          <div className="faq-hero-content">
            <h1 className="faq-hero-title">Frequently Asked Questions</h1>
            <p className="faq-hero-subtitle">
              Find answers to the most common questions about flights, bookings, and payments.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="faq-accordion-section">
        <div className="page-container">
          <div className="faq-categories">
            {filteredCategories.map((category) => (
              <div key={category.id} className="faq-category">
                <div className="faq-category-header">
                  <div className="faq-category-icon">{category.icon}</div>
                  <h2 className="faq-category-title">{category.title}</h2>
                </div>
                
                <div className="faq-list">
                  {category.faqs.map((faq) => {
                    const faqKey = `${category.id}-${faq.id}`;
                    const isOpen = openFAQ === faqKey;
                    
                    return (
                      <div key={faq.id} className="faq-item">
                        <button
                          className={`faq-question ${isOpen ? 'open' : ''}`}
                          onClick={() => toggleFAQ(category.id, faq.id)}
                        >
                          <span className="faq-question-text">{faq.question}</span>
                          <span className="faq-toggle-icon">
                            {isOpen ? '−' : '+'}
                          </span>
                        </button>
                        {isOpen && (
                          <div className="faq-answer">
                            <p className="faq-answer-text">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPage;








